<template>
	<TransitionGroup name="slide" tag="div" class="c-business-tax-fields flex flex-wrap gap-3">
		<template v-if="organizationType === 'Business'">
			<ElCheckbox
				v-if="hasRequiredTaxIds"
				key="TaxRequiredCheckbox"
				v-model="internalIsVATRegistrationOptOut"
				class="c-checkbox-fancy mt-2 w-full -mb-3"
				@change="emitIsVATRegistrationOptOut"
			>
				My business is not registered for {{ taxRegistrationName }}
			</ElCheckbox>
			<label v-for="tax in visibleTaxIdOptions" :key="tax.type" class="field">
				<strong class="label">{{ tax.name }}</strong>
				<CValidatedInput
					:value="taxIds.find(x => x.Type === tax.type)?.Id || ''"
					:vid="`tax-id-${tax.type}`"
					:name="tax.name"
					type="text"
					:rules="{ required: tax.required && !internalIsVATRegistrationOptOut, 'tax-id': { type: tax.type, country: countryCode } }"
					validation-mode="lazy"
					:immediate="!!taxIds.find(x => x.Type === tax.type)?.Id"
					:input-transform="val => normalizeTaxId(tax.type, val)"
					@input="handleTaxIdInput($event, tax.type)"
					@change="emitTaxId($event, tax.type)"
				>
					<template v-if="!tax.required || internalIsVATRegistrationOptOut" #help-text>
						<div>Optional</div>
					</template>
					<template #error="{ errors }">
						<CCallout v-if="errors[0]" type="error" size="s">
							{{ errors[0] }}
						</CCallout>
						<CCallout v-else-if="taxIdInvalid" type="error" size="s">
							Invalid {{ tax.name }}.
						</CCallout>
					</template>
				</CValidatedInput>
			</label>
		</template>
		<CInput
			id="po-number"
			key="PONumber"
			:value="purchaseOrderNumber || ''"
			label="Purchase Order #"
			type="text"
			@input="updatePurchaseOrderNumber"
		>
			<template #help-text>
				<div>Optional</div>
			</template>
		</CInput>
	</TransitionGroup>
</template>

<script lang="ts">
	import CInput from 'src/components/form-controls/Input.vue';
	import CValidatedInput from 'src/components/form-controls/ValidatedInput.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import { Checkbox } from '@cognitoforms/element-ui';
	import type { TaxId } from '@cognitoforms/types/server-types/plans/model/tax-id.ts';
	import type { PropType } from 'vue';
	import { defineComponent, computed, watch, ref, shallowRef } from 'vue';
	import { validateTaxId, normalizeTaxId } from 'src/utilities/tax-id/tax-id-validation';
	import { getTaxIdConfiguration } from 'src/utilities/tax-id/tax-id-configuration';
	import { TaxIdStatus } from '@cognitoforms/types/server-types/plans/model/tax-id-status';
	import { AddressStatus } from '@cognitoforms/types/server-types/plans/model/address-status';
	import { BusinessNameStatus } from '@cognitoforms/types/server-types/plans/model/business-name-status';
	import { AppInsights } from 'src/components/embeds/AppInsights';

	export default defineComponent({
		name: 'CBusinessTaxFields',
		components: {
			CInput,
			CValidatedInput,
			CCallout,
			ElCheckbox: Checkbox
		},
		props: {
			organizationType: {
				type: String,
				validator: (val: string) => ['Business', 'Individual'].includes(val),
				default: 'Business'
			},
			taxIds: {
				type: Array as PropType<TaxId[]>,
				required: false,
				default: () => []
			},
			countryCode: {
				type: String,
				required: true
			},
			state: {
				type: String,
				default: null
			},
			postalCode: {
				type: String,
				default: null
			},
			taxIdInvalid: {
				type: Boolean,
				required: true
			},
			purchaseOrderNumber: {
				type: String,
				default: ''
			},
			isVATRegistrationOptOut: {
				type: Boolean,
				default: false
			}
		},
		emits: [
			'update:tax-id',
			'update:purchase-order-number',
			'update:is-vat-registration-opt-out',
			'reset-tax-id-invalid'
		],
		setup(props, { emit }) {
			const internalTaxIds = ref<TaxId[]>([]);
			const internalIsVATRegistrationOptOut = ref<boolean>(props.isVATRegistrationOptOut);
			watch(() => props.isVATRegistrationOptOut, val => {
				internalIsVATRegistrationOptOut.value = val;
			}, { immediate: true });

			const resetTaxIdInvalid = () => {
				if (props.taxIdInvalid)
					emit('reset-tax-id-invalid');
			};

			const taxIdOptions = shallowRef(getTaxIdConfiguration(props.countryCode, props.state, props.postalCode));
			watch(
				() => [props.countryCode, props.state, props.postalCode] as const,
				([countryCode, state, postalCode], [previousCountryCode]) => {
					const newOptions = getTaxIdConfiguration(countryCode, state, postalCode);
					const oldOptions = taxIdOptions.value;

					const countryChanged = countryCode !== previousCountryCode;
					const typesChanged = newOptions.length !== oldOptions.length ||
						newOptions.some((opt, i) => opt.type !== oldOptions[i]?.type);

					if (countryChanged || typesChanged)
						taxIdOptions.value = newOptions;
				}
			);

			const createDefaultTaxId = (existing?: Partial<TaxId>, type?: string, id?: string): TaxId => ({
				Id: id ?? existing?.Id ?? '',
				Status: existing?.Status ?? TaxIdStatus.Pending,
				AddressStatus: existing?.AddressStatus ?? AddressStatus.Unverified,
				BusinessNameStatus: existing?.BusinessNameStatus ?? BusinessNameStatus.Unverified,
				AuthorityBusinessName: existing?.AuthorityBusinessName ?? '',
				AuthorityAddress: existing?.AuthorityAddress ?? '',
				Type: type ?? existing?.Type ?? '',
				IgnoreUpdates: false,
				ExpirationDate: existing?.ExpirationDate ?? null,
				LastVerifiedDate: existing?.LastVerifiedDate ?? null,
				NextValidationDate: existing?.NextValidationDate ?? null,
				ValidationNote: existing?.ValidationNote ?? null,
				VerificationSource: existing?.VerificationSource ?? null
			});

			const emitTaxId = (val: string, taxType: string) => {
				let existingIdx = internalTaxIds.value.findIndex(x => x.Type === taxType);
				const existing = existingIdx >= 0 ? internalTaxIds.value[existingIdx] : null;
				if (existing?.Id === val)
					return;

				const newTaxId = createDefaultTaxId({ ...existing, Status: TaxIdStatus.Pending }, taxType, val);
				if (existingIdx >= 0) {
					internalTaxIds.value[existingIdx] = newTaxId;
				}
				else {
					internalTaxIds.value.push(newTaxId);
					existingIdx = internalTaxIds.value.length - 1;
				}

				// Validate tax ID using validateTaxId
				const validationResult = validateTaxId(taxType, val, props.countryCode);

				// If the tax ID was normalized, update the value
				if (validationResult.normalizedValue)
					internalTaxIds.value[existingIdx].Id = validationResult.normalizedValue;

				// Log validation failures to AppInsights
				if (val && !validationResult.valid) {
					const appInsights = AppInsights();
					if (appInsights) {
						appInsights.trackTrace({
							message: 'TaxIdValidationFailed',
							properties: {
								taxIdType: taxType,
								taxIdValue: val.replace(/\d/g, '*'),
								countryCode: props.countryCode,
								reason: validationResult.reason
							}
						});
					}
				}

				// Only emit if all tax IDs are valid or empty
				const allTaxIdsValid = internalTaxIds.value.every((taxId, i) => {
					if (!taxId.Id) return true; // Empty is allowed
					const validation = validateTaxId(taxId.Type, taxId.Id, props.countryCode);
					return validation.valid;
				});

				if (allTaxIdsValid) {
					emit('update:tax-id', internalTaxIds.value);
				}
			};

			// Sync internalTaxIds with prop
			watch(
				() => props.taxIds,
				(newVal) => {
					internalTaxIds.value = Array.isArray(newVal) && newVal.length > 0 ? newVal.slice() : [];
				},
				{ immediate: true }
			);

			// If taxIdOptions changes (e.g. country changes), reset internalTaxIds
			watch(
				taxIdOptions,
				(options) => {
					const arr = internalTaxIds.value.filter(x => options.some(o => o.type === x.Type)).map(x => createDefaultTaxId(null, x.Type, x.Id));
					internalTaxIds.value = arr;

					// Reset VAT registration opt-out when location changes
					internalIsVATRegistrationOptOut.value = false;
					emit('update:is-vat-registration-opt-out', false);

					emit('update:tax-id', arr);
				}
			);

			const emitIsVATRegistrationOptOut = (val: boolean) => {
				// Clear required tax IDs when opting out of VAT
				if (val === true) {
					const optionalWhenOptedOutTypes = taxIdOptions.value
						.filter(opt => opt.optionalWhenOptedOut)
						.map(opt => opt.type);

					internalTaxIds.value = internalTaxIds.value.filter(taxId =>
						optionalWhenOptedOutTypes.includes(taxId.Type)
					);
					emit('update:tax-id', internalTaxIds.value);
				}
				emit('update:is-vat-registration-opt-out', val);
			};

			return {
				taxIdOptions,
				internalTaxIds,
				internalIsVATRegistrationOptOut,
				hasRequiredTaxIds: computed(() => {
					return taxIdOptions.value?.some(tax => tax.required) || false;
				}),
				taxRegistrationName: computed(() => {
					const requiredTaxType = taxIdOptions.value?.find(tax => tax.required)?.type;
					if (!requiredTaxType || !requiredTaxType.includes('_'))
						return 'VAT';
					else if (requiredTaxType === 'au_abn')
						return 'GST';
					const withoutCountryCode = requiredTaxType.substring(requiredTaxType.indexOf('_') + 1);
					return withoutCountryCode.replace(/_/g, '/').toUpperCase();
				}),
				visibleTaxIdOptions: computed(() => {
					if (!taxIdOptions.value) return [];
					else
						return internalIsVATRegistrationOptOut.value
							? taxIdOptions.value.filter((tax => tax.optionalWhenOptedOut))
							: taxIdOptions.value;
				}),
				emitIsVATRegistrationOptOut,
				handleTaxIdInput: (val: string, type: string) => {
					if (internalTaxIds.value.find(x => x.Type === type)?.Id !== val)
						resetTaxIdInvalid();
				},
				emitTaxId,
				resetTaxIdInvalid,
				updatePurchaseOrderNumber: (val: string) => {
					emit('update:purchase-order-number', val);
				},
				normalizeTaxId
			};
		}
	});
</script>

<style lang="scss">
	.c-business-tax-fields {

		> *:not(.w-full) {
			width: calc(50% - var(--spacing-2));
		}
	}
</style>
